/* Voz do Jarvis, usando o que o próprio navegador oferece.

   ESCUTAR: a API de reconhecimento de fala existe no Chrome, no Edge e no Safari.
   Atenção, e isto é dito ao usuário na interface: no Chrome e no Edge o áudio é
   enviado para servidores do fabricante do navegador para ser transcrito.

   FALAR: a síntese de voz roda no próprio aparelho, sem enviar nada.

   Nada aqui conversa com um modelo de IA: isso chega na Etapa 2. Até lá, o Jarvis
   entende apenas uma lista curta e explícita de comandos locais. */

import { useSyncExternalStore } from 'react';
import { prefsStore } from './prefs';

type Listener = () => void;

export interface VoiceState {
  supported: { listen: boolean; speak: boolean };
  listening: boolean;
  speaking: boolean;
  /* Nível do microfone, de 0 a 1, para a interface reagir à sua voz. */
  level: number;
  /* O que está sendo dito agora (ainda pode mudar) e o que já foi fechado. */
  interim: string;
  final: string;
  error: string;
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string; continuous: boolean; interimResults: boolean; maxAlternatives: number;
  start(): void; stop(): void; abort(): void;
  onresult: ((e: { resultIndex: number; results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }> }) => void) | null;
  onerror: ((e: { error: string }) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

const W = window as unknown as {
  SpeechRecognition?: new () => SpeechRecognitionLike;
  webkitSpeechRecognition?: new () => SpeechRecognitionLike;
};
const Recognition = W.SpeechRecognition ?? W.webkitSpeechRecognition;

const ERRORS: Record<string, string> = {
  'not-allowed': 'Permissão do microfone negada. Libere o microfone para este site nas permissões do navegador.',
  'service-not-allowed': 'O navegador bloqueou o serviço de transcrição.',
  'no-speech': 'Não ouvi nada. Tente de novo.',
  'audio-capture': 'Nenhum microfone encontrado.',
  network: 'Falha de rede ao transcrever.',
  aborted: ''
};

let state: VoiceState = {
  supported: { listen: Boolean(Recognition), speak: typeof speechSynthesis !== 'undefined' },
  listening: false, speaking: false, level: 0, interim: '', final: '', error: ''
};

const listeners = new Set<Listener>();
const emit = () => listeners.forEach((l) => l());
function set(patch: Partial<VoiceState>) { state = { ...state, ...patch }; emit(); }

let rec: SpeechRecognitionLike | null = null;
let audio: { ctx: AudioContext; stream: MediaStream; raf: number } | null = null;

/* Medidor de nível: um fluxo de microfone próprio, só para a interface reagir. */
async function startLevelMeter() {
  if (audio || !navigator.mediaDevices?.getUserMedia) return;
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const ctx = new AudioContext();
    const src = ctx.createMediaStreamSource(stream);
    const node = ctx.createAnalyser();
    node.fftSize = 512;
    src.connect(node);
    const buf = new Uint8Array(node.frequencyBinCount);
    const tick = () => {
      node.getByteFrequencyData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) sum += buf[i]! * buf[i]!;
      const rms = Math.sqrt(sum / buf.length) / 255;
      const level = Math.min(1, rms * 2.6);
      if (Math.abs(level - state.level) > 0.015) set({ level });
      audio!.raf = requestAnimationFrame(tick);
    };
    audio = { ctx, stream, raf: requestAnimationFrame(tick) };
  } catch {
    /* Sem medidor: a escuta continua funcionando, só não há reação visual ao volume. */
  }
}

function stopLevelMeter() {
  if (!audio) return;
  cancelAnimationFrame(audio.raf);
  audio.stream.getTracks().forEach((t) => t.stop());
  void audio.ctx.close();
  audio = null;
  set({ level: 0 });
}

export const voice = {
  get: () => state,
  subscribe(l: Listener) { listeners.add(l); return () => { listeners.delete(l); }; },

  async listen() {
    if (!Recognition || state.listening) return;
    const r = new Recognition();
    r.lang = 'pt-BR';
    r.continuous = false;
    r.interimResults = true;
    r.maxAlternatives = 1;
    r.onstart = () => set({ listening: true, error: '', interim: '', final: '' });
    r.onresult = (e) => {
      let interim = '';
      let final = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const res = e.results[i]!;
        if (res.isFinal) final += res[0].transcript;
        else interim += res[0].transcript;
      }
      set({ interim, final: final || state.final });
    };
    r.onerror = (e) => set({ error: ERRORS[e.error] ?? `Falha no reconhecimento (${e.error}).` });
    r.onend = () => { rec = null; stopLevelMeter(); set({ listening: false, interim: '' }); };
    rec = r;
    void startLevelMeter();
    try { r.start(); } catch { set({ listening: false }); }
  },

  stop() { rec?.stop(); },

  /* Fala um texto inteiro, cancelando o que estiver em andamento. */
  speak(text: string) {
    const s = voice.speaker();
    s.push(text);
    s.end();
  },

  /* Fala por frases, à medida que o texto chega: a voz começa já na primeira frase,
     em vez de esperar a resposta inteira. Frases curtas também evitam o corte que
     alguns navegadores fazem em falas longas. */
  speaker() {
    if (state.supported.speak) speechSynthesis.cancel();
    queued = 0;
    let buf = '';
    let first = true;
    return {
      push(t: string) {
        if (!state.supported.speak) return;
        buf += t;
        for (;;) {
          const min = first ? 12 : 40;
          const re = /[.!?…;:](?=\s)|\n/g;
          let m: RegExpExecArray | null;
          let cut = -1;
          while ((m = re.exec(buf))) { if (m.index + 1 >= min) { cut = m.index + 1; break; } }
          if (cut < 0) break;
          say(buf.slice(0, cut));
          buf = buf.slice(cut);
          first = false;
        }
      },
      end() { if (buf.trim()) say(buf); buf = ''; }
    };
  },

  hush() { if (state.supported.speak) { speechSynthesis.cancel(); queued = 0; set({ speaking: false }); } },
  clear() { set({ interim: '', final: '', error: '' }); }
};

/* ---------- Escolha da voz ----------
   O navegador traz as vozes do sistema. No Edge há vozes neurais naturais em
   português (a masculina é "Antonio"); no Chrome, a masculina é a "Daniel", mais
   robótica. Sem escolha sua, o Jarvis prefere: masculina, natural, do Brasil. */

const MALE = /antonio|donato|f[aá]bio|humberto|j[uú]lio|nicolau|val[eé]rio|leonardo|daniel|ricardo|thiago|duarte|cristiano|\bmale\b|masculin/i;
const NATURAL = /natural|neural|online|premium|enhanced/i;

export function listVoices(): SpeechSynthesisVoice[] {
  if (!state.supported.speak) return [];
  return speechSynthesis.getVoices().filter((v) => v.lang?.toLowerCase().startsWith('pt'));
}

/* Nomes com acento (Antônio) comparados sem acento. */
const plain = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '');
export function isMaleVoice(v: SpeechSynthesisVoice) { return MALE.test(plain(v.name)); }
export function isNaturalVoice(v: SpeechSynthesisVoice) { return NATURAL.test(v.name); }

/* Português do Brasil? O idioma informado pelo navegador nem sempre vem certo,
   então o nome da voz também conta ("Brazil"/"Brasil" contra "Portugal"). */
export function isBrazilianVoice(v: SpeechSynthesisVoice) {
  if (/portugal/i.test(v.name)) return false;
  return /pt[-_]br/i.test(v.lang) || /brazil|brasil/i.test(v.name);
}

export function pickVoice(preferred = prefsStore.get().voiceName): SpeechSynthesisVoice | undefined {
  const pt = listVoices();
  if (preferred) { const v = pt.find((x) => x.name === preferred); if (v) return v; }
  /* Sempre do Brasil quando existir alguma; entre elas, masculina e natural primeiro. */
  const br = pt.filter(isBrazilianVoice);
  const pool = br.length ? br : pt;
  const score = (v: SpeechSynthesisVoice) =>
    (isMaleVoice(v) ? 100 : 0) + (isNaturalVoice(v) ? 20 : 0) + (/antonio/i.test(plain(v.name)) ? 5 : 0);
  return [...pool].sort((a, b) => score(b) - score(a))[0];
}

let queued = 0;

function clean(s: string) {
  return s.replace(/```[\s\S]*?```/g, ' ').replace(/https?:\/\/\S+/g, ' ').replace(/\[(.+?)\]\(.+?\)/g, '$1').replace(/[*_#`>|]+/g, '').replace(/\s+/g, ' ').trim();
}

function say(chunk: string) {
  const text = clean(chunk);
  if (!text) return;
  const u = new SpeechSynthesisUtterance(text);
  const v = pickVoice();
  u.lang = v?.lang ?? 'pt-BR';
  if (v) u.voice = v;
  /* Vozes naturais soam melhor sem mexer no tom; as antigas ficam mais graves e calmas. */
  const natural = v ? isNaturalVoice(v) : false;
  u.rate = natural ? 1.04 : 1.0;
  u.pitch = natural ? 1 : v && isMaleVoice(v) ? 0.9 : 0.8;
  queued++;
  u.onstart = () => set({ speaking: true });
  u.onend = u.onerror = () => { queued = Math.max(0, queued - 1); if (!queued) set({ speaking: false }); };
  speechSynthesis.speak(u);
}

export function useVoiceState(): VoiceState {
  return useSyncExternalStore(voice.subscribe, voice.get);
}

/* A lista de vozes costuma chegar depois do carregamento. */
if (state.supported.speak) speechSynthesis.onvoiceschanged = () => { /* apenas aquece a lista */ };

/* ---------- Comandos locais ----------
   Propositalmente curtos e explícitos. Tudo o que não estiver aqui é recusado com
   honestidade, em vez de ser inventado. */

export interface LocalFacts {
  eventosHoje?: number;
  aguardando?: number;
  projetosAtivos?: number;
  conectado: { banco: boolean; login: boolean; ia: boolean; google: boolean };
}

export interface LocalAnswer { text: string; known: boolean }

const DIAS = ['domingo', 'segunda-feira', 'terça-feira', 'quarta-feira', 'quinta-feira', 'sexta-feira', 'sábado'];

function norm(s: string) {
  return s.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
}

export const LOCAL_COMMANDS = [
  'que horas são',
  'que dia é hoje',
  'o que tenho hoje',
  'o que espera minha confirmação',
  'o que está conectado',
  'meus projetos'
];

export function answerLocally(said: string, facts: LocalFacts): LocalAnswer {
  const q = norm(said);
  const has = (...w: string[]) => w.every((x) => q.includes(norm(x)));

  if (has('hora')) {
    const d = new Date();
    return { text: `São ${d.getHours()} horas e ${d.getMinutes()} minutos.`, known: true };
  }
  if (has('dia') && (has('hoje') || has('que dia'))) {
    const d = new Date();
    return { text: `Hoje é ${DIAS[d.getDay()]}, dia ${d.getDate()}.`, known: true };
  }
  if (has('hoje') || has('agenda') || has('compromisso')) {
    if (facts.eventosHoje == null) {
      return { text: 'Ainda não há calendário conectado, então não sei o que você tem hoje. A agenda entra na Etapa 5.', known: true };
    }
    return { text: facts.eventosHoje === 0 ? 'Você não tem compromissos hoje.' : `Você tem ${facts.eventosHoje} compromissos hoje.`, known: true };
  }
  if (has('confirmacao') || has('aguarda') || has('esperando') || has('pendente')) {
    const n = facts.aguardando ?? 0;
    return { text: n === 0 ? 'Nada espera a sua confirmação agora.' : `${n} ${n === 1 ? 'item espera' : 'itens esperam'} a sua confirmação. Nada foi enviado.`, known: true };
  }
  if (has('conectado') || has('conexao') || has('status')) {
    const c = facts.conectado;
    const on = [c.banco && 'banco de dados', c.login && 'login com dois fatores', c.ia && 'modelo de IA', c.google && 'conta Google'].filter(Boolean) as string[];
    const off = [!c.ia && 'modelo de IA', !c.google && 'conta Google'].filter(Boolean) as string[];
    return { text: `Estão ativos: ${on.join(', ')}. Ainda não conectados: ${off.join(' e ')}.`, known: true };
  }
  if (has('projeto')) {
    const n = facts.projetosAtivos;
    if (n == null) return { text: 'Não consegui ler seus projetos agora.', known: true };
    return { text: n === 0 ? 'Você não tem projetos ativos.' : `Você tem ${n} ${n === 1 ? 'projeto ativo' : 'projetos ativos'}.`, known: true };
  }
  return {
    text: 'Ainda não sei responder isso. Por enquanto só entendo comandos locais; a conversa de verdade chega quando o modelo de IA for conectado, na Etapa 2.',
    known: false
  };
}
